const toSnakeCase = (str: string) =>
  str
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s_]+/g, '_')
    .replace(/-/g, '_')
    .toLowerCase();

const toKebabCase = (str: string) =>
  str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();

export const toTitleCase = (str: string) => {
  const useRegex = false;
  return useRegex
    ? str.replace(/\w\S*/g, function (text: string) {
        return text.charAt(0).toUpperCase() + text.substr(1).toLowerCase();
      })
    : str
        .split(' ')
        .map(
          (word: string) => word[0].toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ');
};

export const toSentenceCase = (str: string, ignoreWords: string[] = []) => {
  if (!str) return str;

  // Split into words while preserving separators
  const words = str.trim().split(/(\s+)/);

  return words
    .map((word, index) => {
      // Keep whitespace as is
      if (/^\s+$/.test(word)) {
        return word;
      }

      // Check if word starts with any ignore word
      const shouldIgnore = ignoreWords.some(ignoreWord =>
        word.startsWith(ignoreWord),
      );

      if (shouldIgnore) {
        return word;
      }

      // If word is all uppercase (like API, URL), keep it as is
      if (
        word.length > 1 &&
        word === word.toUpperCase() &&
        /[A-Z]/.test(word)
      ) {
        return word;
      }

      // First non-whitespace word: capitalize first letter, rest lowercase
      const isFirstWord = words.slice(0, index).every(w => /^\s+$/.test(w));
      if (isFirstWord) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }

      // Other words: all lowercase
      return word.toLowerCase();
    })
    .join('');
};

export function lowerCase<T extends string>(str?: T) {
  return str?.toLowerCase() as Lowercase<T>;
}

export function capitalize<T extends string>(str: T) {
  return (str.charAt(0).toUpperCase() + str.slice(1)) as Capitalize<T>;
}

export function objectKeysToLowerCase(data: any): any {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => objectKeysToLowerCase(item));
  }

  return Object.keys(data).reduce(
    (result, key) => {
      const value = data[key];

      // Only convert first letter to lowercase if the key has at least one character
      const transformedKey =
        key.length > 0 ? key.charAt(0).toLowerCase() + key.slice(1) : key;

      result[transformedKey] = objectKeysToLowerCase(value);
      return result;
    },
    {} as Record<string, any>,
  );
}
